import {
  HD_LOCKS_KEYS_BIO_NODES_SECTION_KEYS,
  HD_LOCKS_KEYS_BIO_RAVE_SECTION_KEYS,
  HD_LOCKS_KEYS_BIO_SHIFT_SECTION_KEYS,
  HD_LOCKS_KEYS_POST_SHIFT_SECTION_KEYS,
  HD_LOCKS_KEYS_ROUND_END_SECTION_KEYS,
} from './hd-locks-keys-metadata';

/** De vervolgpanelen op de Locks & Keys-uitleg (leesflow vanuit het intro-paneel). */
export type HdLocksKeysReadingPanelId = 'postShift' | 'bioShift' | 'bioRave' | 'bioNodes' | 'roundEnd';

export interface HdLocksKeysReadingPanelDef {
  readonly id: HdLocksKeysReadingPanelId;
  /** Sleutel van het zwevende/gedockte paneel ({@link FloatingPanelService}). */
  readonly panelKey: string;
  /** Font Awesome-icoon (solid) in de paneeltitel en op de doorklikkaart. */
  readonly icon: string;
  /**
   * i18n-prefix van de paneelteksten in de `locks-keys`-scope: `<prefix>.title`,
   * `.tabLabel`, `.linkLabel`, optioneel `.linkHint` (onder de kaartlabel),
   * `.lead` (uitgelicht kader bovenaan het paneel) en `.inlineLinkLabel`
   * (doorlink onder een intro-sectie).
   */
  readonly textPrefix: string;
  /** i18n-prefix van de secties: `<sectionPrefix>.<sectionKey>.*`. */
  readonly sectionPrefix: string;
  readonly sectionKeys: readonly string[];
  /** Uitgelicht: opvallende doorklikkaart (met hint) en lead-kader in het paneel. */
  readonly highlighted: boolean;
}

/** Volgorde = volgorde van de doorklikkaarten onderaan het intro-paneel. */
export const HD_LOCKS_KEYS_READING_PANELS: readonly HdLocksKeysReadingPanelDef[] = [
  {
    id: 'postShift',
    panelKey: 'locks-keys-post-shift',
    icon: 'seedling',
    textPrefix: 'postShift',
    sectionPrefix: 'postShift',
    sectionKeys: HD_LOCKS_KEYS_POST_SHIFT_SECTION_KEYS,
    highlighted: true,
  },
  {
    id: 'bioShift',
    panelKey: 'locks-keys-bio-shift',
    icon: 'dna',
    textPrefix: 'bio.panels.shift',
    sectionPrefix: 'bio',
    sectionKeys: HD_LOCKS_KEYS_BIO_SHIFT_SECTION_KEYS,
    highlighted: false,
  },
  {
    id: 'bioRave',
    panelKey: 'locks-keys-bio-rave',
    icon: 'circle-nodes',
    textPrefix: 'bio.panels.rave',
    sectionPrefix: 'bio',
    sectionKeys: HD_LOCKS_KEYS_BIO_RAVE_SECTION_KEYS,
    highlighted: false,
  },
  {
    id: 'bioNodes',
    panelKey: 'locks-keys-bio-nodes',
    icon: 'compass',
    textPrefix: 'bio.panels.nodes',
    sectionPrefix: 'bio',
    sectionKeys: HD_LOCKS_KEYS_BIO_NODES_SECTION_KEYS,
    highlighted: false,
  },
  {
    id: 'roundEnd',
    panelKey: 'locks-keys-round-end',
    icon: 'hourglass-end',
    textPrefix: 'roundEnd',
    sectionPrefix: 'roundEnd',
    sectionKeys: HD_LOCKS_KEYS_ROUND_END_SECTION_KEYS,
    highlighted: false,
  },
];

const READING_PANEL_BY_ID = new Map<HdLocksKeysReadingPanelId, HdLocksKeysReadingPanelDef>(
  HD_LOCKS_KEYS_READING_PANELS.map(panel => [panel.id, panel]),
);

export function getHdLocksKeysReadingPanel(id: HdLocksKeysReadingPanelId): HdLocksKeysReadingPanelDef {
  const panel = READING_PANEL_BY_ID.get(id);
  if (panel == null) {
    throw new Error(`Onbekend Locks & Keys-vervolgpaneel: ${id}`);
  }
  return panel;
}

/**
 * Intro-secties (`intro.<key>`) die onderaan een doorlink naar een
 * vervolgpaneel dragen: de secties over 19/49 en de kinderen van 2027 wijzen
 * naar "Mens na 2027", die over het einde van de Ronde naar "Het einde van de Ronde".
 */
export const HD_LOCKS_KEYS_INTRO_SECTION_LINKS: Readonly<Partial<Record<string, HdLocksKeysReadingPanelId>>> = {
  indicators: 'postShift',
  channelShift: 'postShift',
  projectorKey: 'postShift',
  endOfRound: 'roundEnd',
  threeQuarters: 'roundEnd',
};
